import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  apiRbacGetSummary,
  apiRbacGetRoleMatrix,
  apiRbacListPermissions,
  apiRbacListStaff,
  apiRbacGetUserDetails,
  apiRbacAssignRole,
  apiRbacBulkAssignRole,
  apiRbacGrantPermissionOverride,
  apiRbacRevokePermissionOverride,
  apiRbacToggleActivation,
  apiRbacGetAuditLog,
  apiRbacCreatePermission,
  apiRbacResetStaffPassword,
  apiRbacSendStaffResetLink,
} from "../api/index.js";
import { getUserFromLocalStorage } from "../utilities/user.js";
import { usePermissions } from "../utilities/usePermissions.js";
import AppSelect from "../components/ui/AppSelect";
import { ModalShell, ModalFooter } from "../components/team";
// How roles, overrides and the settings permissions work: Settings/help/accessHelp.js.
import SettingHelp from "./Settings/settingsHelp";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const ROLES = ["SUPERUSER", "ADMIN", "SUPERVISOR", "CREATOR", "CONSUMER"];

const ROLE_META = {
  SUPERUSER:  { color: "#7c3aed", bg: "#ede9fe", label: "Super User" },
  ADMIN:      { color: "#1d4ed8", bg: "#dbeafe", label: "Admin" },
  SUPERVISOR: { color: "#0369a1", bg: "#e0f2fe", label: "Supervisor" },
  CREATOR:    { color: "#047857", bg: "#d1fae5", label: "Creator" },
  CONSUMER:   { color: "#92400e", bg: "#fef3c7", label: "Consumer" },
};

const DOMAIN_META = {
  users:         { label: "Users",         color: "#1d4ed8" },
  vendors:       { label: "Vendors",       color: "#047857" },
  orders:        { label: "Orders",        color: "#b45309" },
  riders:        { label: "Riders",        color: "#be185d" },
  wallet:        { label: "Wallet",        color: "#065f46" },
  content:       { label: "Content",       color: "#7c3aed" },
  analytics:     { label: "Analytics",     color: "#0369a1" },
  settings:      { label: "Settings",      color: "#374151" },
  notifications: { label: "Notifications", color: "#c2410c" },
  system:        { label: "System",        color: "#dc2626" },
};

const ACTION_LOG_META = {
  ROLE_ASSIGNED:      { label: "Role Assigned",      color: "#1d4ed8" },
  ROLE_REVOKED:       { label: "Role Revoked",       color: "#dc2626" },
  PERMISSION_GRANTED: { label: "Permission Granted", color: "#047857" },
  PERMISSION_REVOKED: { label: "Permission Revoked", color: "#dc2626" },
  USER_ACTIVATED:     { label: "Access Reinstated", color: "#047857" },
  USER_DEACTIVATED:   { label: "Access Suspended",  color: "#b45309" },
  STAFF_OFFBOARDED:   { label: "Staff Offboarded",  color: "#dc2626" },
  STAFF_RESTORED:     { label: "Staff Restored",    color: "#0369a1" },
  USER_CREATED:       { label: "User Created",       color: "#1d4ed8" },
  USER_DELETED:       { label: "User Deleted",       color: "#dc2626" },
  ROLE_CREATED:       { label: "Role Created",       color: "#7c3aed" },
  PERMISSION_CREATED: { label: "Permission Created", color: "#7c3aed" },
  BULK_ROLE_ASSIGNED: { label: "Bulk Role Assigned", color: "#b45309" },
};

const PERM_ACTIONS = ["read", "create", "update", "delete", "manage", "export", "approve", "assign"];

// ---------------------------------------------------------------------------
// STYLES -- single source of truth; all components reference S.*
// ---------------------------------------------------------------------------

const S = {
  page:         { background: "#f8fafc", minHeight: "100vh", padding: "24px 20px", fontFamily: "'Inter', system-ui, sans-serif" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  title:        { fontSize: 26, fontWeight: 700, color: "#0f172a", margin: 0 },
  subtitle:     { fontSize: 13, color: "#64748b", marginTop: 4 },
  tabBar:       { display: "flex", gap: 4, background: "#fff", borderRadius: 10, padding: 4, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 24, flexWrap: "wrap" },
  tab:          (active) => ({ padding: "9px 18px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: active ? "#1d4ed8" : "transparent", color: active ? "#fff" : "#475569", transition: "all 0.15s" }),
  card:         { background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", padding: 20, marginBottom: 16 },
  kpiGrid:      { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 14, marginBottom: 24 },
  kpi:          (color) => ({ background: "#fff", borderRadius: 12, padding: "18px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderLeft: `4px solid ${color}` }),
  kpiVal:       { fontSize: 28, fontWeight: 800, color: "#0f172a" },
  kpiLabel:     { fontSize: 12, color: "#64748b", marginTop: 2, fontWeight: 500 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: "#1e293b", marginBottom: 12 },
  badge:        (bg, color) => ({ display: "inline-flex", alignItems: "center", gap: 4, background: bg, color: color, padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.03em" }),
  table:        { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:           { textAlign: "left", padding: "10px 12px", background: "#f1f5f9", fontWeight: 600, color: "#475569", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" },
  td:           { padding: "12px 12px", borderBottom: "1px solid #f1f5f9", color: "#1e293b", verticalAlign: "middle" },
  btnPrimary:   { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnDanger:    { background: "#dc2626", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnGhost:     { background: "transparent", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 7, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnSuccess:   { background: "#047857", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  btnWarning:   { background: "#b45309", color: "#fff", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  input:        { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, width: "100%", outline: "none", boxSizing: "border-box" },
  select:       { border: "1px solid #e2e8f0", borderRadius: 7, padding: "8px 12px", fontSize: 13, background: "#fff", cursor: "pointer", outline: "none", boxSizing: "border-box" },
  label:        { fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4, display: "block" },
  formRow:      { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 },
  modal:        { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20 },
  modalBox:     { background: "#fff", borderRadius: 14, padding: 28, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" },
  modalTitle:   { fontSize: 18, fontWeight: 700, color: "#0f172a", marginBottom: 18 },
  avatar:       (bg) => ({ width: 36, height: 36, borderRadius: "50%", background: bg, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }),
  searchBar:    { display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" },
  pill:         (color, bg) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 12, background: bg, color: color, fontSize: 11, fontWeight: 700 }),
  alert:        (type) => ({ padding: "12px 16px", borderRadius: 8, marginBottom: 14, fontSize: 13, background: type === "error" ? "#fef2f2" : type === "success" ? "#f0fdf4" : "#eff6ff", color: type === "error" ? "#dc2626" : type === "success" ? "#047857" : "#1d4ed8", border: `1px solid ${type === "error" ? "#fecaca" : type === "success" ? "#bbf7d0" : "#bfdbfe"}` }),
  permGrid:     { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8 },
  spinner:      { display: "inline-block", width: 18, height: 18, border: "2px solid #e2e8f0", borderTopColor: "#1d4ed8", borderRadius: "50%", animation: "spin 0.6s linear infinite" },
};

// ---------------------------------------------------------------------------
// HELPERS -- pure functions, no JSX
// ---------------------------------------------------------------------------

const AVATAR_COLORS  = ["#1d4ed8", "#7c3aed", "#047857", "#b45309", "#dc2626", "#0369a1", "#be185d"];
const getAvatarColor = (str = "") => AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];
const getInitials    = (name = "") => name.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "?";
const fmtDate        = (d) => d ? new Date(d).toLocaleString("en-UG", { dateStyle: "medium", timeStyle: "short" }) : "-";
const shortId        = (id = "") => id.toString().slice(-6);
const apiErrMsg      = (e, fallback) => e?.response?.data?.message || fallback;

// ---------------------------------------------------------------------------
// HOOK -- shared async-action state (loading + error)
// ---------------------------------------------------------------------------

const useAsyncAction = () => {
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");
  const run = useCallback(async (fn) => {
    setLoading(true); setErr("");
    try   { await fn(); }
    catch (e) { setErr(apiErrMsg(e, "An error occurred")); }
    finally   { setLoading(false); }
  }, []);
  return { loading, err, setErr, run };
};

// ---------------------------------------------------------------------------
// PRIMITIVE COMPONENTS -- reused across all tabs
// ---------------------------------------------------------------------------

const Spinner = () => <span style={S.spinner} />;

const LoadingCenter = ({ text = "Loading..." }) => (
  <div style={{ textAlign: "center", padding: 30 }}><Spinner /> {text}</div>
);

const Alert = ({ type, msg, onClose }) => msg ? (
  <div style={{ ...S.alert(type), display: "flex", justifyContent: "space-between", alignItems: "center" }}>
    <span>{msg}</span>
    {onClose && (
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "inherit", padding: 0, marginLeft: 10 }}>x</button>
    )}
  </div>
) : null;

const BTN_VARIANTS = { primary: S.btnPrimary, ghost: S.btnGhost, danger: S.btnDanger, success: S.btnSuccess, warning: S.btnWarning };

const Btn = ({ variant = "ghost", size, onClick, disabled, children, style }) => (
  <button
    style={{ ...BTN_VARIANTS[variant], ...(size === "sm" ? { padding: "4px 10px", fontSize: 11 } : {}), ...style }}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const Field = ({ label, children, style }) => (
  <div style={style}>
    <label style={S.label}>{label}</label>
    {children}
  </div>
);

// ModalShell / ModalFooter: the shared, AppDialog-backed pair from components/team.

const Pagination = ({ page, pages, onPage }) => {
  if (pages <= 1) return null;
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
      <Btn disabled={page === 1}     onClick={() => onPage(page - 1)}>Prev</Btn>
      {Array.from({ length: Math.min(pages, 7) }, (_, i) => i + 1).map(p => (
        <Btn key={p} style={p === page ? { background: "#1d4ed8", color: "#fff" } : {}} onClick={() => onPage(p)}>{p}</Btn>
      ))}
      <Btn disabled={page === pages} onClick={() => onPage(page + 1)}>Next</Btn>
    </div>
  );
};

// Three states, not two: "Inactive" used to cover both a colleague who is
// suspended for a week and one who has left the company, which are not the same
// fact. `state` comes from the server (derived from isActive + deletedAt); the
// `active` prop is the fallback for callers that only hold the boolean.
const LIFECYCLE_PILL = {
  ACTIVE:     { label: "Active",     color: "#047857", bg: "#dcfce7" },
  SUSPENDED:  { label: "Suspended",  color: "#b45309", bg: "#fef3c7" },
  OFFBOARDED: { label: "Offboarded", color: "#475569", bg: "#e2e8f0" },
};

const StatusPill = ({ active, state }) => {
  const meta =
    LIFECYCLE_PILL[state] ||
    (active ? LIFECYCLE_PILL.ACTIVE : LIFECYCLE_PILL.SUSPENDED);
  return <span style={S.pill(meta.color, meta.bg)}>{meta.label}</span>;
};

const OverridePill = ({ type }) => (
  <span style={S.pill(type === "GRANT" ? "#047857" : "#dc2626", type === "GRANT" ? "#dcfce7" : "#fee2e2")}>{type}</span>
);

const CodeTag = ({ children }) => (
  <code style={{ fontSize: 11, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>{children}</code>
);

const ProgressBar = ({ label, color, bg, pct, count, minWidth }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
    <span style={{ ...S.badge(bg, color), minWidth: minWidth || 100 }}>{label}</span>
    <div style={{ flex: 1, background: "#f1f5f9", borderRadius: 6, height: 8, overflow: "hidden" }}>
      <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 6, transition: "width 0.5s" }} />
    </div>
    <span style={{ fontSize: 11, color: "#475569", minWidth: 50, textAlign: "right" }}>{count}</span>
  </div>
);

const RoleBadge = ({ role }) => {
  const m = ROLE_META[role] || { color: "#374151", bg: "#f1f5f9", label: role };
  return <span style={S.badge(m.bg, m.color)}>{m.label}</span>;
};

const UserAvatar = ({ name, email }) => {
  const str = name || email || "";
  return <div style={S.avatar(getAvatarColor(str))}>{getInitials(str)}</div>;
};

const RoleSelect = ({ value, onChange, style }) => (
  <AppSelect style={{ ...S.select, width: "100%", ...style }} value={value} onChange={onChange}>
    {ROLES.map(r => <option key={r} value={r}>{ROLE_META[r]?.label || r}</option>)}
  </AppSelect>
);

const KpiCard = ({ color, value, label }) => (
  <div style={S.kpi(color)}>
    <div style={S.kpiVal}>{value}</div>
    <div style={S.kpiLabel}>{label}</div>
  </div>
);

// ---------------------------------------------------------------------------
// TAB: OVERVIEW
// ---------------------------------------------------------------------------

const OverviewTab = ({ summary }) => {
  if (!summary) return <LoadingCenter text="Loading overview..." />;
  const { roleCounts, activeCount, totalStaff, inactiveCount, overrideCount, permissionCount, recentActions, roleHierarchy } = summary;

  return (
    <div>
      <div style={S.kpiGrid}>
        <KpiCard color="#1d4ed8" value={totalStaff}      label="Total Staff" />
        <KpiCard color="#047857" value={activeCount}     label="Active Staff" />
        <KpiCard color="#dc2626" value={inactiveCount}   label="Inactive Staff" />
        <KpiCard color="#7c3aed" value={permissionCount} label="System Permissions" />
        <KpiCard color="#b45309" value={overrideCount}   label="Permission Overrides" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={S.card}>
          <div style={S.sectionTitle}>Role Distribution</div>
          {ROLES.map((role) => {
            const count = (roleCounts || []).find(r => r._id === role)?.count || 0;
            const m     = ROLE_META[role];
            const pct   = totalStaff > 0 ? Math.round((count / totalStaff) * 100) : 0;
            return <ProgressBar key={role} label={m.label} color={m.color} bg={m.bg} pct={pct} count={`${count} (${pct}%)`} minWidth={90} />;
          })}
        </div>

        <div style={S.card}>
          <div style={S.sectionTitle}>Role Hierarchy</div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 10 }}>Higher level = more privilege</div>
          {ROLES.map((role) => {
            const m     = ROLE_META[role];
            const level = roleHierarchy?.[role] || 0;
            return <ProgressBar key={role} label={m.label} color={m.color} bg={m.bg} pct={level} count={`L${level}`} minWidth={90} />;
          })}
        </div>
      </div>

      <div style={S.card}>
        <div style={S.sectionTitle}>Recent RBAC Activity</div>
        {(!recentActions || recentActions.length === 0) ? (
          <p style={{ color: "#94a3b8", fontSize: 13 }}>No recent activity.</p>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Action</th>
                <th style={S.th}>Actor</th>
                <th style={S.th}>Target</th>
                <th style={S.th}>Time</th>
              </tr>
            </thead>
            <tbody>
              {recentActions.map((log) => {
                const m = ACTION_LOG_META[log.action] || { label: log.action, color: "#374151" };
                return (
                  <tr key={log._id}>
                    <td style={S.td}><span style={S.pill(m.color, `${m.color}18`)}>{m.label}</span></td>
                    <td style={S.td}>{log.actorId?.name || log.actorEmail}</td>
                    <td style={S.td}>{log.targetUserId?.email || log.targetUserEmail || "-"}</td>
                    <td style={S.td}>{fmtDate(log.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// MODAL: ASSIGN ROLE
// ---------------------------------------------------------------------------

const AssignRoleModal = ({ user, onClose, onSuccess }) => {
  const [role, setRole]     = useState(user?.user_role || "SUPERVISOR");
  const [reason, setReason] = useState("");
  const { loading, err, setErr, run } = useAsyncAction();

  const handleSubmit = () => {
    if (!role) return setErr("Select a role");
    run(async () => {
      await apiRbacAssignRole(user._id, role, reason);
      onSuccess(`Role updated to ${role} for ${user.name || user.email}`);
      onClose();
    });
  };

  return (
    <ModalShell onClose={onClose}>
      <div style={S.modalTitle}>Assign Role</div>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        Changing role for: <strong>{user.name || user.email}</strong> (current: <RoleBadge role={user.user_role} />)
      </p>
      <Alert type="error" msg={err} onClose={() => setErr("")} />
      <Field label={<>New Role<SettingHelp topic="staffRole" /></>} style={{ marginBottom: 14 }}>
        <RoleSelect value={role} onChange={e => setRole(e.target.value)} />
      </Field>
      <Field label="Reason (optional)" style={{ marginBottom: 16 }}>
        <textarea
          style={{ ...S.input, resize: "vertical", minHeight: 70 }}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. Promoted by CEO decision"
        />
      </Field>
      <ModalFooter>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner /> : "Assign Role"}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// MODAL: RESET PASSWORD  (super-user only)
// ---------------------------------------------------------------------------

const MIN_PASSWORD_LEN = 8;

const ResetPasswordModal = ({ user, onClose, onSuccess }) => {
  const [pw, setPw]             = useState("");
  const [confirm, setConfirm]   = useState("");
  const [show, setShow]         = useState(false);
  const { loading, err, setErr, run } = useAsyncAction();

  const handleSubmit = () => {
    // Validate locally before hitting the API. We deliberately never log or
    // serialise the password values anywhere — only their validity is checked.
    if (pw.length < MIN_PASSWORD_LEN) {
      return setErr(`Password must be at least ${MIN_PASSWORD_LEN} characters`);
    }
    if (pw !== confirm) {
      return setErr("Passwords do not match");
    }
    run(async () => {
      await apiRbacResetStaffPassword(user._id, pw);
      onSuccess(`Password reset for ${user.name || user.email}`);
      onClose();
    });
  };

  // Preferred flow: email the user a set-password link — no plaintext password
  // ever passes through the admin.
  const handleSendLink = () => {
    if (!user.email) return setErr("This staff member has no email on file.");
    run(async () => {
      await apiRbacSendStaffResetLink(user._id);
      onSuccess(`Reset link emailed to ${user.email}`);
      onClose();
    });
  };

  return (
    <ModalShell onClose={onClose}>
      <div style={S.modalTitle}>Reset Password</div>
      <p style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>
        Recommended: email <strong>{user.email || user.name}</strong> a secure
        set-password link (valid 1 hour) — or type a new password for them below.
      </p>
      <div style={{ marginBottom: 16 }}>
        <Btn variant="primary" onClick={handleSendLink} disabled={loading}>
          {loading ? <Spinner /> : "✉ Email Reset Link"}
        </Btn>
      </div>
      <Alert type="error" msg={err} onClose={() => setErr("")} />
      <Field label="New Password" style={{ marginBottom: 12 }}>
        <input
          style={S.input}
          type={show ? "text" : "password"}
          value={pw}
          onChange={e => setPw(e.target.value)}
          placeholder={`At least ${MIN_PASSWORD_LEN} characters`}
          autoComplete="new-password"
        />
      </Field>
      <Field label="Confirm New Password" style={{ marginBottom: 12 }}>
        <input
          style={S.input}
          type={show ? "text" : "password"}
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder="Re-enter the new password"
          autoComplete="new-password"
        />
      </Field>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#475569", marginBottom: 4, cursor: "pointer" }}>
        <input type="checkbox" checked={show} onChange={() => setShow(s => !s)} />
        Show password
      </label>
      <ModalFooter>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner /> : "Reset Password"}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// MODAL: PERMISSION OVERRIDES
// ---------------------------------------------------------------------------

const PermissionOverrideModal = ({ user, allPermissions, onClose, onSuccess }) => {
  const [permKey, setPermKey] = useState("");
  const [type, setType]       = useState("GRANT");
  const [reason, setReason]   = useState("");
  const [userDetails, setUserDetails]       = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const { loading, err, setErr, run } = useAsyncAction();

  const refreshDetails = useCallback(() => {
    setLoadingDetails(true);
    apiRbacGetUserDetails(user._id)
      .then(r => setUserDetails(r.data.data))
      .catch(() => {})
      .finally(() => setLoadingDetails(false));
  }, [user._id]);

  useEffect(() => { refreshDetails(); }, [refreshDetails]);

  const handleGrant = () => {
    if (!permKey) return setErr("Select a permission");
    run(async () => {
      await apiRbacGrantPermissionOverride(user._id, { permissionKey: permKey, type, reason });
      onSuccess(`Permission override ${type} set for ${user.name || user.email}`);
      refreshDetails();
      setPermKey(""); setReason("");
    });
  };

  const handleRevoke = async (pk) => {
    try {
      await apiRbacRevokePermissionOverride(user._id, pk);
      onSuccess(`Override for ${pk} removed`);
      refreshDetails();
    } catch (e) {
      setErr(apiErrMsg(e, "Failed to revoke override"));
    }
  };

  const overrides = userDetails?.permissionOverrides || [];

  return (
    <ModalShell onClose={onClose}>
      <div style={S.modalTitle}>Permission overrides for {user.name || user.email}<SettingHelp topic="staffPermissionOverride" /></div>
      <p style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
        Base role: <RoleBadge role={user.user_role} />. An override replaces the role default for one
        permission: GRANT gives it, DENY takes it away. Overrides do nothing to a Super User, and only a
        Super User can save or remove one.
      </p>
      <Alert type="error" msg={err} onClose={() => setErr("")} />

      {loadingDetails ? <LoadingCenter /> : overrides.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <div style={S.sectionTitle}>Active Overrides ({overrides.length})</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Permission</th>
                <th style={S.th}>Type</th>
                <th style={S.th}>By</th>
                <th style={S.th}>Action</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map(o => (
                <tr key={o._id}>
                  <td style={S.td}><CodeTag>{o.permissionKey}</CodeTag></td>
                  <td style={S.td}><OverridePill type={o.type} /></td>
                  <td style={S.td}>{o.grantedBy?.name || "-"}</td>
                  <td style={S.td}>
                    <Btn variant="danger" size="sm" onClick={() => handleRevoke(o.permissionKey)}>Remove</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ fontSize: 12, color: "#94a3b8", marginBottom: 12 }}>No overrides set, so the role defaults apply.</p>
      )}

      <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 14 }}>
        <div style={S.sectionTitle}>Add Override<SettingHelp topic="staffPermissionOverride" /></div>
        <div style={S.formRow}>
          <Field label="Permission">
            <AppSelect style={{ ...S.select, width: "100%" }} value={permKey} onChange={e => setPermKey(e.target.value)}>
              <option value="">Select permission</option>
              {(allPermissions || []).map(p => (
                <option key={p.key} value={p.key}>[{p.domain}] {p.name}</option>
              ))}
            </AppSelect>
          </Field>
          <Field label="Type">
            <AppSelect style={{ ...S.select, width: "100%" }} value={type} onChange={e => setType(e.target.value)}>
              <option value="GRANT">GRANT</option>
              <option value="DENY">DENY</option>
            </AppSelect>
          </Field>
        </div>
        <Field label="Reason (optional)" style={{ marginBottom: 14 }}>
          <input style={S.input} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for this override" />
        </Field>
        <ModalFooter>
          <Btn onClick={onClose}>Close</Btn>
          <Btn variant="primary" onClick={handleGrant} disabled={loading}>
            {loading ? <Spinner /> : "Apply Override"}
          </Btn>
        </ModalFooter>
      </div>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// MODAL: USER DETAILS
// ---------------------------------------------------------------------------

const UserDetailModal = ({ userId, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiRbacGetUserDetails(userId)
      .then(r => setDetails(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <ModalShell onClose={onClose}><LoadingCenter /></ModalShell>;
  if (!details) return null;

  const { user, defaultPermissions, permissionOverrides } = details;
  const isFullAccess = defaultPermissions[0] === "*";

  return (
    <ModalShell onClose={onClose} maxWidth={640}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <UserAvatar name={user.name} email={user.email} />
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{user.name || "(no name)"}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>{user.email}</div>
          <div style={{ marginTop: 4, display: "flex", gap: 6 }}>
            <RoleBadge role={user.user_role} />
            <StatusPill active={user.isActive} />
          </div>
        </div>
      </div>

      <div style={S.sectionTitle}>Role-Based Permissions</div>
      {isFullAccess ? (
        <div style={{ ...S.alert("success"), marginBottom: 14 }}>
          Full Access - SUPERUSER has unrestricted access to all platform features.
        </div>
      ) : (
        <div style={{ maxHeight: 160, overflow: "auto", marginBottom: 14 }}>
          <div style={S.permGrid}>
            {defaultPermissions.map(pk => (
              <div key={pk} style={{ padding: "5px 10px", borderRadius: 6, background: "#eff6ff", fontSize: 11, color: "#1d4ed8", fontWeight: 600 }}>
                {pk}
              </div>
            ))}
          </div>
        </div>
      )}

      {permissionOverrides.length > 0 && (
        <>
          <div style={S.sectionTitle}>Active Overrides ({permissionOverrides.length})</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
            {permissionOverrides.map(o => (
              <span key={o._id} style={S.pill(o.type === "GRANT" ? "#047857" : "#dc2626", o.type === "GRANT" ? "#dcfce7" : "#fee2e2")}>
                {o.type === "GRANT" ? "+" : "-"} {o.permissionKey}
              </span>
            ))}
          </div>
        </>
      )}

      <div style={{ fontSize: 12, color: "#64748b" }}>
        Last login: {fmtDate(user.lastLogin)}. Member since: {fmtDate(user.createdAt)}.
      </div>
      <ModalFooter><Btn onClick={onClose}>Close</Btn></ModalFooter>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// TAB: USERS & ROLES
// ---------------------------------------------------------------------------

const UsersTab = ({ onToast, currentUser, isSuperUser }) => {
  const [staff, setStaff]                   = useState([]);
  const [loading, setLoading]               = useState(true);
  const [allPermissions, setAllPermissions] = useState([]);
  const [search, setSearch]                 = useState("");
  const [filterRole, setFilterRole]         = useState("");
  const [filterActive, setFilterActive]     = useState("");
  const [page, setPage]                     = useState(1);
  const [pagination, setPagination]         = useState({ total: 0, pages: 1 });
  const [selected, setSelected]             = useState([]);
  const [assignModal, setAssignModal]       = useState(null);
  const [overrideModal, setOverrideModal]   = useState(null);
  const [detailModal, setDetailModal]       = useState(null);
  const [resetModal, setResetModal]         = useState(null);
  const [bulkRole, setBulkRole]             = useState("SUPERVISOR");
  const [bulkLoading, setBulkLoading]       = useState(false);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (search)            params.search   = search;
      if (filterRole)        params.role     = filterRole;
      if (filterActive !== "") params.isActive = filterActive;
      const r = await apiRbacListStaff(params);
      setStaff(r.data.data);
      setPagination(r.data.pagination);
    } catch (e) {
      onToast("error", apiErrMsg(e, "Failed to load staff"));
    } finally { setLoading(false); }
  }, [page, search, filterRole, filterActive]);

  useEffect(() => {
    apiRbacListPermissions().then(r => setAllPermissions(r.data.data)).catch(() => {});
  }, []);

  useEffect(() => { loadStaff(); }, [loadStaff]);

  // Suspending a colleague now carries a mandatory reason: the audit row used
  // to record who and when but never why, because this screen sent "". The
  // server refuses a change without one, so the dialog is not decoration.
  const [activationModal, setActivationModal] = useState(null);

  const handleBulkRole = async () => {
    if (selected.length === 0) return onToast("error", "Select at least one user");
    setBulkLoading(true);
    try {
      const r = await apiRbacBulkAssignRole(selected, bulkRole, "Bulk assignment");
      onToast("success", `${r.data.summary.succeeded}/${r.data.summary.total} users updated`);
      setSelected([]);
      loadStaff();
    } catch (e) {
      onToast("error", apiErrMsg(e, "Bulk assignment failed"));
    } finally { setBulkLoading(false); }
  };

  const toggleSelect    = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = ()   => setSelected(prev => prev.length === staff.length ? [] : staff.map(u => u._id));

  return (
    <div>
      <div style={S.searchBar}>
        <input
          style={{ ...S.input, maxWidth: 240 }}
          placeholder="Search by name or email"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <AppSelect style={{ ...S.select, minWidth: 140 }} value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1); }}>
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{ROLE_META[r]?.label}</option>)}
        </AppSelect>
        <AppSelect style={{ ...S.select, minWidth: 140 }} value={filterActive} onChange={e => { setFilterActive(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </AppSelect>
        <Btn onClick={loadStaff}>Refresh</Btn>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>{pagination.total} staff members</span>
      </div>

      {selected.length > 0 && (
        <div style={{ ...S.card, display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: "#eff6ff", borderRadius: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>{selected.length} selected</span>
          <RoleSelect value={bulkRole} onChange={e => setBulkRole(e.target.value)} style={{ maxWidth: 160 }} />
          <Btn variant="primary" onClick={handleBulkRole} disabled={bulkLoading}>
            {bulkLoading ? <Spinner /> : `Assign Role to ${selected.length}`}
          </Btn>
          <Btn onClick={() => setSelected([])}>Clear</Btn>
        </div>
      )}

      <div style={S.card}>
        {loading ? (
          <LoadingCenter text="Loading staff..." />
        ) : staff.length === 0 ? (
          <p style={{ color: "#94a3b8", textAlign: "center", padding: 20 }}>No staff found</p>
        ) : (
          <>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, width: 36 }}>
                    <input type="checkbox" checked={selected.length === staff.length && staff.length > 0} onChange={toggleSelectAll} />
                  </th>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Staff Member</th>
                  <th style={S.th}>Role</th>
                  <th style={S.th}>Status</th>
                  <th style={S.th}>Last Login</th>
                  <th style={S.th}>Overrides</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((u, idx) => (
                  <tr key={u._id} style={{ background: selected.includes(u._id) ? "#f0f9ff" : "transparent" }}>
                    <td style={S.td}>
                      <input type="checkbox" checked={selected.includes(u._id)} onChange={() => toggleSelect(u._id)} />
                    </td>
                    <td style={{ ...S.td, color: "#94a3b8", fontSize: 12 }}>{(page - 1) * 15 + idx + 1}</td>
                    <td style={S.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <UserAvatar name={u.name} email={u.email} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name || "(no name)"}</div>
                          <div style={{ fontSize: 11, color: "#94a3b8" }}>{u.email}</div>
                          <div style={{ fontSize: 10, color: "#cbd5e1" }}>...{shortId(u._id)}</div>
                        </div>
                      </div>
                    </td>
                    <td style={S.td}><RoleBadge role={u.user_role} /></td>
                    <td style={S.td}><StatusPill active={u.isActive} state={u.lifecycleState} /></td>
                    <td style={{ ...S.td, fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmtDate(u.lastLogin)}</td>
                    <td style={S.td}>
                      <span style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600 }}>{u.permissionOverrides?.length || 0}</span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        <Btn size="sm" onClick={() => setDetailModal(u._id)}>View</Btn>
                        {u._id !== currentUser?._id && (
                          <>
                            <Btn variant="primary" size="sm" onClick={() => setAssignModal(u)}>Role</Btn>
                            <Btn variant="warning" size="sm" onClick={() => setOverrideModal(u)}>Perms</Btn>
                            {/* Reset Password — super-users only. The backend
                                endpoint is itself super-user-gated; this hides
                                the affordance from everyone else. */}
                            {isSuperUser && (
                              <Btn size="sm" onClick={() => setResetModal(u)}>Reset Password</Btn>
                            )}
                            {u.lifecycleState === "OFFBOARDED" ? (
                              <span style={{ fontSize: 11, color: "#94a3b8", alignSelf: "center" }}>
                                Restore from Staff Directory
                              </span>
                            ) : (
                              isSuperUser && (
                                <Btn
                                  variant={u.isActive ? "danger" : "success"}
                                  size="sm"
                                  onClick={() => setActivationModal(u)}
                                >
                                  {u.isActive ? "Suspend" : "Reinstate"}
                                </Btn>
                              )
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pagination.pages} onPage={setPage} />
          </>
        )}
      </div>

      {assignModal && (
        <AssignRoleModal
          user={assignModal}
          onClose={() => setAssignModal(null)}
          onSuccess={(msg) => { onToast("success", msg); loadStaff(); }}
        />
      )}
      {overrideModal && (
        <PermissionOverrideModal
          user={overrideModal}
          allPermissions={allPermissions}
          onClose={() => setOverrideModal(null)}
          onSuccess={(msg) => onToast("success", msg)}
        />
      )}
      {detailModal && (
        <UserDetailModal userId={detailModal} onClose={() => setDetailModal(null)} />
      )}
      {resetModal && (
        <ResetPasswordModal
          user={resetModal}
          onClose={() => setResetModal(null)}
          onSuccess={(msg) => { onToast("success", msg); }}
        />
      )}
      {activationModal && (
        <ActivationModal
          user={activationModal}
          onClose={() => setActivationModal(null)}
          onSuccess={(msg) => { onToast("success", msg); loadStaff(); }}
          onError={(msg) => onToast("error", msg)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// MODAL: SUSPEND / REINSTATE
// ---------------------------------------------------------------------------

const MIN_ACTIVATION_REASON = 5;

const ActivationModal = ({ user, onClose, onSuccess, onError }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);
  const suspending = user.isActive;

  const submit = async () => {
    if (reason.trim().length < MIN_ACTIVATION_REASON) return;
    setBusy(true);
    try {
      const r = await apiRbacToggleActivation(user._id, !user.isActive, reason.trim());
      onSuccess(
        r?.data?.message ||
          `${user.name || user.email} ${suspending ? "suspended" : "reinstated"}`,
      );
      onClose();
    } catch (e) {
      onError(apiErrMsg(e, "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <div style={S.modalTitle}>
        {suspending ? "Suspend access" : "Reinstate access"}
        <SettingHelp topic="staffSuspendAccess" />
      </div>
      <div style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
        <strong>{user.name || user.email}</strong>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>{user.email}</div>
      </div>
      <p style={{ fontSize: 13, color: "#475569", marginBottom: 14 }}>
        {suspending
          ? "Blocks sign-in immediately and closes every session this person currently holds. Their role and overrides are kept, so reinstating them restores the same access."
          : "Restores sign-in with the role and overrides this account already holds."}
      </p>
      <Field label="Reason (required)">
        <textarea
          style={{ ...S.input, minHeight: 72, resize: "vertical" }}
          value={reason}
          autoFocus
          onChange={(e) => setReason(e.target.value)}
          placeholder="Recorded in the audit log against your name"
        />
      </Field>
      <ModalFooter>
        <Btn onClick={onClose} disabled={busy}>Cancel</Btn>
        <Btn
          variant={suspending ? "danger" : "success"}
          onClick={submit}
          disabled={busy || reason.trim().length < MIN_ACTIVATION_REASON}
        >
          {busy ? "Working..." : suspending ? "Suspend" : "Reinstate"}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
};

// ---------------------------------------------------------------------------
// PERMISSION MATRIX
// ---------------------------------------------------------------------------

const PermissionMatrix = ({ matrix, permissions, domainFilter }) => {
  if (!matrix) return null;
  const filtered = domainFilter ? permissions.filter(p => p.domain === domainFilter) : permissions;

  return (
    <div style={{ ...S.card, overflowX: "auto" }}>
      <div style={{ ...S.sectionTitle, marginBottom: 8 }}>Role x Permission Matrix<SettingHelp topic="staffRole" /></div>
      <p style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
        Y means the role holds the permission by default; a dash means it does not. Per person overrides
        are not shown here.
      </p>
      <table style={{ ...S.table, minWidth: 700 }}>
        <thead>
          <tr>
            <th style={{ ...S.th, minWidth: 200 }}>Permission</th>
            {ROLES.map(role => (
              <th key={role} style={{ ...S.th, textAlign: "center", minWidth: 100 }}>
                {ROLE_META[role]?.label || role}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map(perm => (
            <tr key={perm._id}>
              <td style={S.td}>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{perm.name}</div>
                <CodeTag>{perm.key}</CodeTag>
              </td>
              {ROLES.map(role => {
                const rm  = matrix.matrix?.[role];
                const has = rm?.isFullAccess || rm?.permissions?.includes(perm.key);
                return (
                  <td key={role} style={{ ...S.td, textAlign: "center" }}>
                    {has
                      ? <span style={{ color: "#047857", fontWeight: 700 }}>Y</span>
                      : <span style={{ color: "#e2e8f0" }}>-</span>
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TAB: PERMISSIONS
// ---------------------------------------------------------------------------

const EMPTY_PERM_FORM = { key: "", name: "", description: "", domain: "users", action: "read" };

const PermissionsTab = ({ onToast, isSuperUser }) => {
  const [permissions, setPermissions]   = useState([]);
  const [matrix, setMatrix]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [domainFilter, setDomainFilter] = useState("");
  const [view, setView]                 = useState("list");
  const [showCreate, setShowCreate]     = useState(false);
  const [form, setForm]                 = useState(EMPTY_PERM_FORM);
  const { loading: creating, err, setErr, run } = useAsyncAction();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, mr] = await Promise.all([apiRbacListPermissions(), apiRbacGetRoleMatrix()]);
      setPermissions(pr.data.data);
      setMatrix(mr.data.data);
    } catch (e) {
      onToast("error", "Failed to load permissions");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = () => {
    if (!form.key || !form.name || !form.description) return setErr("All fields required");
    run(async () => {
      await apiRbacCreatePermission(form);
      onToast("success", `Permission "${form.key}" created`);
      setShowCreate(false);
      setForm(EMPTY_PERM_FORM);
      load();
    });
  };

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const groupedByDomain = useMemo(() => {
    const filtered = domainFilter ? permissions.filter(p => p.domain === domainFilter) : permissions;
    return Object.entries(
      filtered.reduce((acc, p) => { (acc[p.domain] = acc[p.domain] || []).push(p); return acc; }, {})
    );
  }, [permissions, domainFilter]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn style={view === "list"   ? { background: "#1d4ed8", color: "#fff" } : {}} onClick={() => setView("list")}>Permission List</Btn>
          <Btn style={view === "matrix" ? { background: "#1d4ed8", color: "#fff" } : {}} onClick={() => setView("matrix")}>Role Matrix</Btn>
        </div>
        {isSuperUser && (
          <Btn variant="primary" onClick={() => setShowCreate(true)}>+ New Permission</Btn>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        <Btn style={domainFilter === "" ? { background: "#1d4ed8", color: "#fff" } : {}} onClick={() => setDomainFilter("")}>All</Btn>
        {Object.entries(DOMAIN_META).map(([d, m]) => (
          <Btn
            key={d}
            style={domainFilter === d ? { background: m.color, color: "#fff", borderColor: m.color } : {}}
            onClick={() => setDomainFilter(d)}
          >
            {m.label}
          </Btn>
        ))}
      </div>

      {loading ? (
        <LoadingCenter />
      ) : view === "matrix" ? (
        <PermissionMatrix matrix={matrix} permissions={permissions} domainFilter={domainFilter} />
      ) : (
        <div>
          {groupedByDomain.map(([domain, perms]) => {
            const dm = DOMAIN_META[domain] || { label: domain, color: "#374151" };
            return (
              <div key={domain} style={S.card}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: dm.color }}>
                    {dm.label}
                    {domain === "settings" && <SettingHelp topic="settingsAccessLevels" />}
                    {domain === "access_control" && <SettingHelp topic="accessControlPermissions" />}
                  </span>
                  <span style={S.pill(dm.color, `${dm.color}18`)}>{perms.length}</span>
                </div>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Key</th>
                      <th style={S.th}>Name</th>
                      <th style={S.th}>Action</th>
                      <th style={S.th}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perms.map(p => (
                      <tr key={p._id}>
                        <td style={S.td}><CodeTag>{p.key}</CodeTag></td>
                        <td style={{ ...S.td, fontWeight: 600 }}>{p.name}</td>
                        <td style={S.td}><span style={S.pill(dm.color, `${dm.color}18`)}>{p.action}</span></td>
                        <td style={{ ...S.td, color: "#64748b", fontSize: 12 }}>{p.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <ModalShell onClose={() => setShowCreate(false)}>
          <div style={S.modalTitle}>Create New Permission<SettingHelp topic="staffPermissionCatalogue" /></div>
          <Alert type="error" msg={err} onClose={() => setErr("")} />
          <div style={S.formRow}>
            <Field label="Key (e.g. reports:read)">
              <input style={S.input} value={form.key} onChange={e => setField("key", e.target.value.toLowerCase())} placeholder="domain:action" />
            </Field>
            <Field label="Name">
              <input style={S.input} value={form.name} onChange={e => setField("name", e.target.value)} placeholder="View Reports" />
            </Field>
          </div>
          <div style={S.formRow}>
            <Field label="Domain">
              <AppSelect style={{ ...S.select, width: "100%" }} value={form.domain} onChange={e => setField("domain", e.target.value)}>
                {Object.entries(DOMAIN_META).map(([d, m]) => <option key={d} value={d}>{m.label}</option>)}
              </AppSelect>
            </Field>
            <Field label="Action">
              <AppSelect style={{ ...S.select, width: "100%" }} value={form.action} onChange={e => setField("action", e.target.value)}>
                {PERM_ACTIONS.map(a => <option key={a}>{a}</option>)}
              </AppSelect>
            </Field>
          </div>
          <Field label="Description" style={{ marginBottom: 16 }}>
            <input style={S.input} value={form.description} onChange={e => setField("description", e.target.value)} placeholder="What this permission allows. It does nothing until code checks the key." />
          </Field>
          <ModalFooter>
            <Btn onClick={() => setShowCreate(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? <Spinner /> : "Create"}
            </Btn>
          </ModalFooter>
        </ModalShell>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// TAB: AUDIT LOG
// ---------------------------------------------------------------------------

const EMPTY_FILTERS = { action: "", startDate: "", endDate: "" };

const AuditLogTab = ({ onToast }) => {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [filters, setFilters]       = useState(EMPTY_FILTERS);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (filters.action)    params.action    = filters.action;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate)   params.endDate   = filters.endDate;
      const r = await apiRbacGetAuditLog(params);
      setLogs(r.data.data);
      setPagination(r.data.pagination);
    } catch (e) {
      onToast("error", "Failed to load audit log");
    } finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const setFilter    = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setPage(1); };

  return (
    <div>
      <div style={S.searchBar}>
        <AppSelect
          style={{ ...S.select, minWidth: 190 }}
          value={filters.action}
          onChange={e => setFilter("action", e.target.value)}
        >
          <option value="">All Actions</option>
          {Object.entries(ACTION_LOG_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </AppSelect>
        <input type="date" style={{ ...S.input, maxWidth: 160 }} value={filters.startDate} onChange={e => setFilter("startDate", e.target.value)} />
        <span style={{ fontSize: 13, color: "#94a3b8" }}>to</span>
        <input type="date" style={{ ...S.input, maxWidth: 160 }} value={filters.endDate}   onChange={e => setFilter("endDate",   e.target.value)} />
        <Btn onClick={clearFilters}>Clear</Btn>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>{pagination.total} entries</span>
      </div>

      <div style={S.card}>
        {loading ? (
          <LoadingCenter />
        ) : logs.length === 0 ? (
          <p style={{ color: "#94a3b8", textAlign: "center", padding: 20 }}>No audit entries found</p>
        ) : (
          <>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Action</th>
                  <th style={S.th}>Actor</th>
                  <th style={S.th}>Target</th>
                  <th style={S.th}>Details</th>
                  <th style={S.th}>Outcome</th>
                  <th style={S.th}>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => {
                  const m = ACTION_LOG_META[log.action] || { label: log.action, color: "#374151" };
                  return (
                    <tr key={log._id}>
                      <td style={{ ...S.td, color: "#94a3b8", fontSize: 11 }}>{(page - 1) * 25 + idx + 1}</td>
                      <td style={S.td}>
                        <span style={{ ...S.pill(m.color, `${m.color}18`), fontSize: 11 }}>{m.label}</span>
                      </td>
                      <td style={S.td}>
                        <div style={{ fontWeight: 600, fontSize: 12 }}>{log.actorId?.name || log.actorEmail}</div>
                        <RoleBadge role={log.actorRole} />
                      </td>
                      <td style={S.td}>
                        {log.targetUserId ? (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{log.targetUserId?.name || "-"}</div>
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>{log.targetUserEmail}</div>
                          </>
                        ) : <span style={{ color: "#94a3b8", fontSize: 12 }}>System</span>}
                      </td>
                      <td style={{ ...S.td, fontSize: 11, color: "#64748b", maxWidth: 200 }}>
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <details>
                            <summary style={{ cursor: "pointer", color: "#1d4ed8" }}>View</summary>
                            <pre style={{ margin: "4px 0 0", fontSize: 10, background: "#f8fafc", padding: 6, borderRadius: 4, overflow: "auto" }}>
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        ) : "-"}
                      </td>
                      <td style={S.td}>
                        <StatusPill active={log.outcome === "SUCCESS"} />
                      </td>
                      <td style={{ ...S.td, fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{fmtDate(log.createdAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination page={page} pages={pagination.pages} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TOAST NOTIFICATION
// ---------------------------------------------------------------------------

const Toast = ({ toast, onClear }) => {
  useEffect(() => {
    if (toast.msg) {
      const t = setTimeout(onClear, 4000);
      return () => clearTimeout(t);
    }
  }, [toast.msg, onClear]);

  if (!toast.msg) return null;
  return (
    <div style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 99999,
      background: toast.type === "error" ? "#dc2626" : "#047857",
      color: "#fff", borderRadius: 10, padding: "14px 20px",
      boxShadow: "0 8px 32px rgba(0,0,0,0.18)", fontSize: 14, fontWeight: 600,
      display: "flex", alignItems: "center", gap: 10, maxWidth: 380,
      animation: "fadeInUp 0.2s ease",
    }}>
      <span>{toast.msg}</span>
      <button onClick={onClear} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 18, padding: 0, marginLeft: "auto" }}>x</button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ROOT COMPONENT
// ---------------------------------------------------------------------------

const TABS = [
  { id: "overview",    label: "Overview" },
  { id: "users",       label: "Users & Roles" },
  { id: "permissions", label: "Permissions" },
  { id: "audit",       label: "Audit Log" },
];

const RBACManager = () => {
  const currentUser = getUserFromLocalStorage()?.result;
  const isSuperUser = currentUser?.user_role === "SUPERUSER";

  // Current user's EFFECTIVE permissions (from GET /api/rbac/me/permissions via
  // the PermissionsProvider). Used to surface "my access" in the Overview header.
  const { permissions: myPermissions, isSuperUser: myIsSuper, loaded: permsLoaded } = usePermissions();
  const myPermCount = Array.isArray(myPermissions)
    ? myPermissions.filter((p) => p !== "*").length
    : 0;

  const [activeTab, setActiveTab]           = useState("overview");
  const [summary, setSummary]               = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [toast, setToast]                   = useState({ type: "success", msg: "" });

  const onToast = useCallback((type, msg) => setToast({ type, msg }), []);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const r = await apiRbacGetSummary();
      setSummary(r.data.data);
    } catch (e) {
      onToast("error", apiErrMsg(e, "Failed to load RBAC summary"));
    } finally { setSummaryLoading(false); }
  }, [onToast]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div style={S.page}>
      <style>{`
        @keyframes spin     { to { transform: rotate(360deg); } }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      <div style={S.header}>
        <div>
          <h1 style={S.title}>Access Control<SettingHelp topic="staffAccessControl" placement="bottom" /></h1>
          <div style={S.subtitle}>Each staff member's role, the per person overrides on top of it, and a record of every change.</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {permsLoaded && (
            <span style={S.badge("#e0f2fe", "#0369a1")} title="Your effective permissions">
              {myIsSuper ? "Full access" : `${myPermCount} permissions`}
            </span>
          )}
          {isSuperUser && <span style={S.badge("#ede9fe", "#7c3aed")}>SUPERUSER</span>}
          <Btn onClick={loadSummary}>
            {summaryLoading ? <Spinner /> : "Refresh"}
          </Btn>
        </div>
      </div>

      <div style={S.tabBar}>
        {TABS.map(t => (
          <button key={t.id} style={S.tab(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "overview"    && <OverviewTab summary={summary} />}
      {activeTab === "users"       && <UsersTab onToast={onToast} currentUser={currentUser} isSuperUser={isSuperUser} />}
      {activeTab === "permissions" && <PermissionsTab onToast={onToast} isSuperUser={isSuperUser} />}
      {activeTab === "audit"       && <AuditLogTab onToast={onToast} />}

      <Toast toast={toast} onClear={() => setToast({ type: "success", msg: "" })} />
    </div>
  );
};

export default RBACManager;
